// ============================================================================
// Document side panel — the tabbed panel beside a text note's / report's text
// ============================================================================
// Questions · Checklist · Changes · Previous year · Review notes, each with a
// count. Replaces the panels that used to stack above the editor (they took
// the text's height). The tabs stay on ONE row: those that do not fit go
// under the Enterprise UI's 3-dot action menu (ActionMenu), which shows their
// combined count; the active tab is always on the row. Closed, the panel shrinks to a strip of tab
// buttons. Whether it is open, and the tab last used, are remembered per user.

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ActionMenu, type ActionMenuItem } from 'components/ui';

export interface SidePanelTab {
  id: string;
  label: string;
  /** One or two characters for the closed strip. */
  short: string;
  count?: number | null;
  /** Amber count (something to act on) rather than grey. */
  warn?: boolean;
  content: React.ReactNode;
}

interface Props {
  tabs: SidePanelTab[];
  active: string;
  onActive: (id: string) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const OPEN_KEY = 'fsa.docEditor.panelOpen';
const TAB_KEY = 'fsa.docEditor.panelTab';
/** Room kept at the row's end for the close button. */
const CLOSE_WIDTH = 34;

/** The remembered panel state (defaults: open, first tab). */
export function readPanelPrefs(): { open: boolean; tab: string | null } {
  try {
    return { open: localStorage.getItem(OPEN_KEY) !== '0', tab: localStorage.getItem(TAB_KEY) };
  } catch {
    return { open: true, tab: null };
  }
}

export function writePanelPrefs(open: boolean, tab: string) {
  try {
    localStorage.setItem(OPEN_KEY, open ? '1' : '0');
    localStorage.setItem(TAB_KEY, tab);
  } catch { /* storage unavailable */ }
}

const TabLabel: React.FC<{ tab: SidePanelTab }> = ({ tab }) => (
  <>
    {tab.label}
    {tab.count != null && tab.count > 0 && (
      <span style={{ ...countStyle, background: tab.warn ? '#fef3c7' : 'var(--bg-secondary)', color: tab.warn ? '#92400e' : 'var(--text-secondary)' }}>
        {tab.count}
      </span>
    )}
  </>
);

const DocumentSidePanel: React.FC<Props> = ({ tabs, active, onActive, open, onOpenChange }) => {
  const current = tabs.find(t => t.id === active) ?? tabs[0];

  // ---- one-row tabs: measure each tab, fit what the row holds -------------
  const rowRef = useRef<HTMLDivElement>(null);
  const measureRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const moreMeasureRef = useRef<HTMLButtonElement>(null);
  const [rowWidth, setRowWidth] = useState(0);
  const [widths, setWidths] = useState<Record<string, number>>({});
  const [moreWidth, setMoreWidth] = useState(80);
  const measureKey = tabs.map(t => `${t.id}:${t.label}:${t.count ?? ''}`).join('|');

  useLayoutEffect(() => {
    if (!open) return;
    const next: Record<string, number> = {};
    for (const t of tabs) next[t.id] = measureRefs.current[t.id]?.offsetWidth ?? 0;
    setWidths(next);
    if (moreMeasureRef.current) setMoreWidth(moreMeasureRef.current.offsetWidth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measureKey, open]);

  useEffect(() => {
    const el = rowRef.current;
    if (!open || !el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(entries => setRowWidth(entries[0].contentRect.width));
    ro.observe(el);
    setRowWidth(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, [open]);

  const { visible, hidden } = useMemo(() => {
    const avail = rowWidth - CLOSE_WIDTH;
    const total = tabs.reduce((sum, t) => sum + (widths[t.id] ?? 0), 0);
    if (!rowWidth || total <= avail) return { visible: tabs, hidden: [] as SidePanelTab[] };
    const vis: SidePanelTab[] = [];
    let used = moreWidth;
    for (const t of tabs) {
      const w = widths[t.id] ?? 0;
      if (used + w > avail) break;
      vis.push(t);
      used += w;
    }
    // The tab being viewed always stays on the row.
    if (current && !vis.includes(current)) {
      if (vis.length > 0) vis[vis.length - 1] = current;
      else vis.push(current);
    }
    return { visible: vis, hidden: tabs.filter(t => !vis.includes(t)) };
  }, [tabs, widths, rowWidth, moreWidth, current]);

  const hiddenCount = hidden.reduce((sum, t) => sum + (t.count ?? 0), 0);
  const hiddenWarn = hidden.some(t => t.warn && (t.count ?? 0) > 0);
  const moreItems: ActionMenuItem[] = hidden.map(t => ({
    id: t.id,
    label: t.count ? `${t.label} (${t.count})` : t.label,
    onClick: () => onActive(t.id),
  }));

  if (!open) {
    return (
      <aside style={stripStyle} aria-label="Document panel (closed)">
        <button type="button" style={iconBtnStyle} onClick={() => onOpenChange(true)} title="Open the panel" aria-label="Open the panel">
          <IconSystem.chevronLeft size={16} />
        </button>
        {tabs.map(t => (
          <button
            key={t.id}
            type="button"
            title={t.count ? `${t.label} (${t.count})` : t.label}
            aria-label={t.label}
            onClick={() => { onActive(t.id); onOpenChange(true); }}
            style={stripTabStyle}
          >
            {t.short}
            {!!t.count && <span style={{ ...stripDotStyle, background: t.warn ? '#d97706' : '#94a3b8' }} />}
          </button>
        ))}
      </aside>
    );
  }

  return (
    <aside style={panelStyle} aria-label="Document panel">
      {/* Off-screen copies — their natural widths decide what fits on the row. */}
      <div style={measureRowStyle} aria-hidden="true">
        {tabs.map(t => (
          <button key={t.id} type="button" tabIndex={-1} ref={el => { measureRefs.current[t.id] = el; }} style={tabStyle}>
            <TabLabel tab={t} />
          </button>
        ))}
        <button type="button" tabIndex={-1} ref={moreMeasureRef} style={{ ...tabStyle, padding: '8px 4px' }}>
          <IconSystem.moreVertical size={16} /><span style={countStyle}>99</span>
        </button>
      </div>

      <div ref={rowRef} style={tabsRowStyle} role="tablist">
        {visible.map(t => {
          const on = t.id === current?.id;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={on}
              onClick={() => onActive(t.id)}
              style={{
                ...tabStyle,
                color: on ? 'var(--primary-color, #1d4ed8)' : 'var(--text-secondary)',
                borderBottomColor: on ? 'var(--primary-color, #1d4ed8)' : 'transparent',
              }}
            >
              <TabLabel tab={t} />
            </button>
          );
        })}
        {hidden.length > 0 && (
          <span style={moreWrapStyle} title={`More: ${hidden.map(t => t.label).join(', ')}`}>
            <ActionMenu
              id="doc-panel-more"
              items={moreItems}
              position="bottom-left"
              triggerContent={<IconSystem.moreVertical size={16} />}
            />
            {hiddenCount > 0 && (
              <span style={{ ...countStyle, background: hiddenWarn ? '#fef3c7' : 'var(--bg-secondary)', color: hiddenWarn ? '#92400e' : 'var(--text-secondary)' }}>
                {hiddenCount}
              </span>
            )}
          </span>
        )}
        <span style={{ flex: 1 }} />
        <button type="button" style={iconBtnStyle} onClick={() => onOpenChange(false)} title="Close the panel" aria-label="Close the panel">
          <IconSystem.chevronRight size={16} />
        </button>
      </div>
      <div style={bodyStyle} role="tabpanel">{current?.content}</div>
    </aside>
  );
};

const panelStyle: React.CSSProperties = {
  position: 'relative', width: 360, flexShrink: 0, display: 'flex', flexDirection: 'column', minHeight: 0,
  borderLeft: '1px solid var(--border-color)', background: 'var(--bg-primary)',
};
const tabsRowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', flexWrap: 'nowrap', gap: 0, padding: '2px 4px 0',
  borderBottom: '1px solid var(--border-color)', minHeight: 40, boxSizing: 'border-box',
};
const measureRowStyle: React.CSSProperties = {
  position: 'absolute', visibility: 'hidden', pointerEvents: 'none', top: 0, left: 0,
  display: 'flex', whiteSpace: 'nowrap', height: 0, overflow: 'hidden',
};
const tabStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 5, padding: '8px 8px 9px', border: 'none',
  borderBottom: '2px solid transparent', background: 'transparent', cursor: 'pointer',
  fontSize: '0.8rem', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0,
};
const countStyle: React.CSSProperties = { fontSize: '0.68rem', fontWeight: 700, padding: '0 6px', borderRadius: 999 };
const moreWrapStyle: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 2, flexShrink: 0, padding: '0 4px' };
const bodyStyle: React.CSSProperties = { flex: 1, minHeight: 0, overflowY: 'auto', padding: '12px 14px' };
const iconBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, flexShrink: 0,
  border: 'none', borderRadius: 6, background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer',
};
const stripStyle: React.CSSProperties = {
  width: 46, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, paddingTop: 8,
  borderLeft: '1px solid var(--border-color)', background: 'var(--bg-secondary)',
};
const stripTabStyle: React.CSSProperties = {
  position: 'relative', width: 32, height: 32, borderRadius: 8, border: '1px solid var(--border-color)',
  background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer',
};
const stripDotStyle: React.CSSProperties = { position: 'absolute', top: 3, right: 3, width: 7, height: 7, borderRadius: 999 };

export default DocumentSidePanel;
